'use client'

import { useEffect, useMemo, useState } from 'react'
import { apiFetch } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { toast } from 'sonner'
import { Calculator, CheckCircle2, Eye, FileText, Loader2, RefreshCw, Save, Wallet, Download, FileDown } from 'lucide-react'
import type { MonthlyReport, Tier } from '@/types/monthly'
import { format } from 'date-fns'
import { PageShell } from '@/components/ui/page-shell'
import { SectionHeader } from '@/components/ui/section-header'
import { GlassCard, GlassCardHeader, GlassCardTitle } from '@/components/ui/glass-card'
import { StatusPill } from '@/components/ui/status-pill'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Label } from '@/components/ui/label'
import { EmployeeStipendReports } from './EmployeeStipendReports'
import { exportStipendReportDoc } from '@/lib/export-stipend-report'
import { exportPayslip as exportPayslipLib } from '@/lib/export-payslip'
import { invoiceDataFromReport, invoiceDataFromCalculation, exportStipendInvoicePdf } from '@/lib/export-stipend-invoice'
import { computeSalary, workingDaysInRange } from '@/lib/stipend-calc'
import { isWeekendYMD } from '@/lib/date-utils'

const TIER_TONE: Record<Tier, Parameters<typeof StatusPill>[0]['tone']> = {
  EXCELLENT: 'green',
  GOOD: 'cyan',
  AVERAGE: 'amber',
  LOW: 'red',
}

/** Roles that appear in the stipend workflow — mirrors STIPEND_ROLES on the server. */
const STIPEND_ROLES = ['EMPLOYEE', 'INTERN', 'HR', 'ADMIN']

function rupee(value: number | null | undefined) {
  return `₹${Math.round(value || 0).toLocaleString('en-IN')}`
}

/** Next calendar day as YYYY-MM-DD, computed in UTC so IST never shifts it. */
function addDayISO(ymd: string): string {
  const d = new Date(ymd + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() + 1)
  return d.toISOString().slice(0, 10)
}

/** "Ravi Kumar (EMP014) — HR: Priya Sharma" */
function employeeOptionLabel(u: any): string {
  const identity = u.role === 'HR' || u.role === 'ADMIN'
    ? `${u.name} (${u.role === 'ADMIN' ? 'Admin' : 'HR'})`
    : `${u.name} (${u.employeeId || 'No ID'})`
  return u.assignedHrName ? `${identity} — HR: ${u.assignedHrName}` : identity
}

export function MonthlyReports() {
  const [month, setMonth] = useState(format(new Date(), 'yyyy-MM'))
  const [reports, setReports] = useState<MonthlyReport[]>([])
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [active, setActive] = useState<MonthlyReport | null>(null)
  const [finalInput, setFinalInput] = useState('')
  const [deductionInput, setDeductionInput] = useState('0')
  const [savingFinal, setSavingFinal] = useState(false)
  // When set, the per-employee stipend history replaces this whole screen.
  const [historyUser, setHistoryUser] = useState<{ id: string; name: string } | null>(null)

  // Salary Calculator States
  const [users, setUsers] = useState<any[]>([])
  const [leaves, setLeaves] = useState<any[]>([])
  const [wfhRequests, setWfhRequests] = useState<any[]>([])
  const [holidayDates, setHolidayDates] = useState<Set<string>>(new Set())
  const [calcUserId, setCalcUserId] = useState('')
  const [calcAllocatedSalary, setCalcAllocatedSalary] = useState('')
  const [calcLeavesCount, setCalcLeavesCount] = useState('0')
  const [calcReductions, setCalcReductions] = useState('0')
  const [calcAttendanceRecords, setCalcAttendanceRecords] = useState<any[]>([])
  const [loadingAttendance, setLoadingAttendance] = useState(false)
  const [calcStartDate, setCalcStartDate] = useState(() => `${format(new Date(), 'yyyy-MM')}-01`)
  const [calcEndDate, setCalcEndDate] = useState(() => format(new Date(), 'yyyy-MM-dd'))
  const [savingCalc, setSavingCalc] = useState(false)

  const calcSelectedUser = useMemo(() => users.find(u => u.id === calcUserId) || null, [users, calcUserId])

  const finalizedCount = reports.filter(r => r.finalStipend != null).length

  const load = async () => {
    setLoading(true)
    try {
      const res = await apiFetch(`/api/performance/monthly?month=${month}`)
      const data = await res.json()
      setReports(data.reports || [])
    } catch (e: any) {
      toast.error(e.message || 'Failed to load monthly reports')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [month])

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await apiFetch('/api/users')
        const data = await res.json()
        // Interns are paid through this workflow too, and HR/Admin staff appear
        // so their pay can be calculated here. Inactive accounts never show.
        if (data.users) setUsers(data.users.filter((u: any) => STIPEND_ROLES.includes(u.role) && u.isActive !== false))
      } catch (e) {
        console.error(e)
      }
    }
    const fetchLeaves = async () => {
      try {
        const res = await apiFetch('/api/leave')
        const data = await res.json()
        if (data.leaves) {
          setLeaves(data.leaves)
        }
      } catch (e) {
        console.error(e)
      }
    }
    const fetchHolidays = async () => {
      try {
        const res = await apiFetch('/api/holidays')
        const data = await res.json()
        if (data.holidays) {
          setHolidayDates(new Set(data.holidays.map((h: any) => String(h.date).slice(0, 10)).filter(Boolean)))
        }
      } catch (e) {
        console.error(e)
      }
    }
    const fetchWfh = async () => {
      try {
        const res = await apiFetch('/api/wfh')
        const data = await res.json()
        if (data.wfhRequests) {
          setWfhRequests(data.wfhRequests)
        }
      } catch (e) {
        console.error(e)
      }
    }
    fetchUsers()
    fetchLeaves()
    fetchHolidays()
    fetchWfh()
  }, [])

  useEffect(() => {
    if (!calcUserId) return
    const fetchCalcAttendance = async () => {
      setLoadingAttendance(true)
      try {
        if (!calcStartDate || !calcEndDate || calcStartDate > calcEndDate) {
          setCalcAttendanceRecords([])
          setCalcLeavesCount('0')
          return
        }
        const res = await apiFetch(`/api/attendance?userId=${calcUserId}&startDate=${calcStartDate}&endDate=${calcEndDate}`)
        const data = await res.json()
        if (data.attendance) {
          setCalcAttendanceRecords(data.attendance)

          const userLeaves = leaves.filter((l: any) => l.userId === calcUserId && l.status === 'APPROVED')
          const uniqueLeaveDays = new Set<string>()
          userLeaves.forEach((leave: any) => {
            // String-based day stepping with a UTC-midnight Date purely as a
            // counter — the old toISOString() conversion shifted every day to
            // the previous date for IST users, silently undercounting leaves
            // that began on the range's first day.
            for (let d = leave.startDate; d <= leave.endDate; d = addDayISO(d)) {
              // Only working days count as leave: a Sunday or official holiday
              // inside a leave span was never payable, so deducting it would
              // double-count the day against the employee.
              if (d >= calcStartDate && d <= calcEndDate && !isWeekendYMD(d) && !holidayDates.has(d)) {
                uniqueLeaveDays.add(d)
              }
            }
          })
          setCalcLeavesCount(String(uniqueLeaveDays.size))
        }
      } catch (e) {
        console.error(e)
      } finally {
        setLoadingAttendance(false)
      }
    }
    fetchCalcAttendance()
  }, [calcUserId, calcStartDate, calcEndDate, leaves, holidayDates])

  // Working days for the selected range: calendar days minus Sundays and
  // official holidays — the denominator of the daily stipend rate.
  const calcWorkingDays = useMemo(
    () => workingDaysInRange(calcStartDate, calcEndDate, holidayDates),
    [calcStartDate, calcEndDate, holidayDates],
  )

  // Day keys covered by approved WFH for the selected employee, clamped to range.
  const calcWfhDates = useMemo(() => {
    const days = new Set<string>()
    const userWfh = wfhRequests.filter((w: any) => w.userId === calcUserId && w.status === 'APPROVED')
    for (const w of userWfh) {
      if (!w.startDate || !w.endDate) continue
      for (let d = w.startDate; d <= w.endDate && d <= calcEndDate; d = addDayISO(d)) {
        if (d >= calcStartDate) days.add(d)
      }
    }
    return days
  }, [wfhRequests, calcUserId, calcStartDate, calcEndDate])

  // Present days: working days in range with a check-in (or an HR-set PRESENT
  // override), or covered by approved WFH — the monthly report counts an
  // approved-WFH day without a check-in as present (WFH_NO_LOG), so this
  // calculator must too or the two reports disagree. Check-ins on
  // Sundays/holidays are extra credit and must not offset absences.
  const calcPresentDays = useMemo(() => {
    const presentSet = new Set<string>(calcWfhDates)
    for (const r of calcAttendanceRecords) {
      const day = String(r.date || '').slice(0, 10)
      if (day >= calcStartDate && day <= calcEndDate && (!!r.checkIn || r.status === 'PRESENT')) {
        presentSet.add(day)
      }
    }
    let count = 0
    for (const d of presentSet) {
      if (d >= calcStartDate && d <= calcEndDate && !isWeekendYMD(d) && !holidayDates.has(d)) count++
    }
    return count
  }, [calcAttendanceRecords, calcWfhDates, calcStartDate, calcEndDate, holidayDates])

  // Hoisted out of the results panel so the Save button writes exactly the
  // numbers on screen rather than recomputing them.
  const calc = useMemo(() => computeSalary({
    startDate: calcStartDate,
    endDate: calcEndDate,
    allocatedSalary: Number(calcAllocatedSalary),
    leavesTaken: Number(calcLeavesCount),
    reductions: Number(calcReductions),
    workingDays: calcWorkingDays,
    presentDays: calcPresentDays,
  }), [calcStartDate, calcEndDate, calcAllocatedSalary, calcLeavesCount, calcReductions, calcWorkingDays, calcPresentDays])

  const saveCalculation = async () => {
    if (!calcUserId) return toast.error('Select an employee first')
    if (calc.allocatedSalary <= 0) return toast.error('Enter the allocated salary before saving')
    if (calc.totalDays <= 0) return toast.error('Pick a valid date range before saving')

    setSavingCalc(true)
    try {
      const res = await apiFetch('/api/performance/stipend-calculations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: calcUserId,
          startDate: calcStartDate,
          endDate: calcEndDate,
          allocatedSalary: calc.allocatedSalary,
          workingDays: calc.workingDays,
          presentDays: calc.presentDays,
          absentDays: calc.absentDays,
          leavesTaken: calc.leavesTaken,
          reductions: calc.reductions,
          totalDays: calc.totalDays,
          dailyRate: calc.dailyRate,
          absenceDeduction: calc.absenceDeduction,
          earnedPay: calc.earnedPay,
          totalPay: calc.totalPay,
        }),
      })
      const data = await res.json()
      toast.success(
        data.reportUpdated
          ? `Saved — ${rupee(calc.totalPay)} also written to the ${calcStartDate.slice(0, 7)} report`
          : `Saved ${rupee(calc.totalPay)}${data.reportSkippedReason ? ` · report not updated: ${data.reportSkippedReason}` : ''}`
      )
      load()
    } catch (e: any) {
      toast.error(e.message || 'Failed to save the calculation')
    } finally {
      setSavingCalc(false)
    }
  }

  const exportPayslip = (report: any) => {
    const emp = users.find(u => u.id === report.userId)
    try {
      exportPayslipLib(report, emp || {})
    } catch (e: any) {
      toast.error(e.message || 'Could not open the payslip window')
    }
  }

  // Invoice for the calculation currently on screen — the exact figures HR is
  // looking at, straight from the same computeSalary result the Save button uses.
  const downloadCurrentInvoice = async () => {
    if (!calcUserId) return toast.error('Select an employee first')
    if (calc.allocatedSalary <= 0) return toast.error('Enter the allocated salary first')
    try {
      await exportStipendInvoicePdf(invoiceDataFromCalculation({
        month: calcStartDate.slice(0, 7),
        startDate: calcStartDate,
        endDate: calcEndDate,
        workingDays: calc.workingDays,
        presentDays: calc.presentDays,
        absentDays: calc.absentDays,
        dailyRate: calc.dailyRate,
        allocatedSalary: calc.allocatedSalary,
        absenceDeduction: calc.absenceDeduction,
        earnedPay: calc.earnedPay,
        reductions: calc.reductions,
        totalPay: calc.totalPay,
      }, calcSelectedUser || { name: 'Employee' }))
      toast.success('Invoice PDF downloaded')
    } catch (e: any) {
      toast.error(e.message || 'Could not generate the invoice')
    }
  }

  // Individual PDF stipend invoice — reuses the report's stored attendance and
  // calculator figures; no new records are created.
  const exportInvoice = async (report: MonthlyReport) => {
    const emp = users.find(u => u.id === report.userId)
    try {
      await exportStipendInvoicePdf(invoiceDataFromReport(report, emp || { name: report.userName }))
      toast.success('Invoice PDF downloaded')
    } catch (e: any) {
      toast.error(e.message || 'Could not generate the invoice')
    }
  }

  const generate = async (userIds?: string[]) => {
    if (!userIds && !confirm(`Generate stipend reports for ${month}? This may take several minutes.`)) return
    setGenerating(true)
    try {
      const res = await apiFetch('/api/performance/monthly/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month, userIds }),
      })
      const data = await res.json()
      toast.success(`Processed ${data.processed} reports`)
      load()
      if (userIds) setActive(null)
    } catch (e: any) {
      toast.error(e.message || 'Failed to generate reports')
    } finally {
      setGenerating(false)
    }
  }

  // Amount actually written: the entered figure minus any manual deduction.
  const finalPayable = useMemo(() => {
    const base = Number(finalInput)
    const deduction = Number(deductionInput) || 0
    if (!Number.isFinite(base)) return NaN
    return Math.max(0, base - deduction)
  }, [finalInput, deductionInput])

  const openReport = (report: MonthlyReport) => {
    setActive(report)
    setFinalInput(String(report.finalStipend ?? report.suggestedStipend ?? 0))
    setDeductionInput('0')
  }

  const saveFinalStipend = async () => {
    if (!active) return
    if (!Number.isFinite(finalPayable) || finalPayable < 0) return toast.error('Enter a valid non-negative amount')

    setSavingFinal(true)
    try {
      await apiFetch('/api/performance/monthly', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: active.id, finalStipend: finalPayable }),
      })
      toast.success(`Saved final stipend ${rupee(finalPayable)}`)
      load()
      setActive(null)
    } catch (e: any) {
      toast.error(e.message || 'Save failed')
    } finally {
      setSavingFinal(false)
    }
  }

  const approveSuggested = async (report: MonthlyReport) => {
    try {
      await apiFetch('/api/performance/monthly', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: report.id, finalStipend: report.suggestedStipend }),
      })
      toast.success(`Approved stipend for ${report.userName}`)
      load()
      setActive(null)
    } catch (e: any) {
      toast.error(e.message || 'Approval failed')
    }
  }

  if (historyUser) {
    const employee = users.find(u => u.id === historyUser.id)
    return (
      <EmployeeStipendReports
        employee={{ ...(employee || {}), id: historyUser.id, name: employee?.name || historyUser.name }}
        onBack={() => setHistoryUser(null)}
      />
    )
  }

  return (
    <PageShell>
      <SectionHeader
        icon={Wallet}
        title="Stipend Workflow"
        description="Generate, audit, and finalize monthly employee stipends"
        action={
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="w-full sm:w-[170px]" />
        }
      />

      {/* HR Salary Management */}
      <GlassCard tone="orange" inset className="mb-6 overflow-hidden">
        <div className="flex items-center gap-3 border-b border-orange-500/15 bg-gradient-to-r from-orange-500/10 to-transparent px-5 py-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-orange-500/15 ring-1 ring-inset ring-orange-500/25">
            <Calculator className="h-4.5 w-4.5 text-orange-600 dark:text-orange-300" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold tracking-tight text-neutral-900 dark:text-white/90">HR Salary Management</h3>
            <p className="text-xs text-neutral-500 dark:text-white/50">Calculate an employee&apos;s salary for any date range from their attendance</p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 p-5">
          {/* Left: Configuration */}
          <div className="lg:col-span-5 space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-neutral-400 dark:text-white/40">Select Employee / HR</label>
              <select
                value={calcUserId}
                onChange={e => setCalcUserId(e.target.value)}
                className="w-full h-9 rounded-md border border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-950 px-3 text-sm text-neutral-900 dark:text-white shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-orange-500 focus-visible:ring-orange-500/50 focus-visible:ring-[3px]"
              >
                <option value="">-- Choose Employee / HR --</option>
                {users.map(u => (
                  <option key={u.id} value={u.id} className="bg-white text-neutral-900 dark:bg-neutral-900 dark:text-white">
                    {employeeOptionLabel(u)}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-400 dark:text-white/40">From Date</label>
                <Input type="date" value={calcStartDate} onChange={e => setCalcStartDate(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-400 dark:text-white/40">To Date</label>
                <Input type="date" value={calcEndDate} min={calcStartDate} onChange={e => setCalcEndDate(e.target.value)} />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-neutral-400 dark:text-white/40">Allocated Salary (₹)</label>
              <Input
                type="number"
                value={calcAllocatedSalary}
                onChange={e => setCalcAllocatedSalary(e.target.value)}
                placeholder="e.g. 30000"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-400 dark:text-white/40">Leaves Taken</label>
                <Input
                  type="number"
                  value={calcLeavesCount}
                  onChange={e => setCalcLeavesCount(e.target.value)}
                  placeholder="e.g. 0"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-400 dark:text-white/40">Reductions (₹)</label>
                <Input
                  type="number"
                  value={calcReductions}
                  onChange={e => setCalcReductions(e.target.value)}
                  placeholder="e.g. 0"
                />
              </div>
            </div>
          </div>

          {/* Right: Results */}
          <div className="lg:col-span-7">
            <div className="space-y-4">
              {/* Hero — Total Calculated Pay */}
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 p-5 text-white shadow-lg shadow-orange-500/20">
                <div className="pointer-events-none absolute -right-6 -top-8 h-28 w-28 rounded-full bg-white/10 blur-2xl" />
                <div className="relative flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-white/80">Total Calculated Pay</p>
                    <p className="mt-1 text-3xl font-bold tabular-nums">{rupee(calc.totalPay)}</p>
                    <p className="mt-1 truncate text-[11px] text-white/70">
                      {calcSelectedUser
                        ? `${calcSelectedUser.name} · ${calcStartDate} → ${calcEndDate}`
                        : 'Select an employee to calculate salary'}
                    </p>
                    {calcSelectedUser?.assignedHrName && (
                      <p className="truncate text-[11px] text-white/70">Assigned HR: {calcSelectedUser.assignedHrName}</p>
                    )}
                  </div>
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-inset ring-white/20">
                    <Wallet className="h-5 w-5" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Attendance Analysis */}
                <div className="rounded-xl ring-1 ring-inset ring-neutral-200 dark:ring-white/10 bg-neutral-50 dark:bg-white/5 p-4">
                  <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-white/40">Attendance</h4>
                  {!calcUserId ? (
                    <p className="py-6 text-center text-xs text-neutral-400 dark:text-white/30">Select an employee to analyze attendance.</p>
                  ) : loadingAttendance ? (
                    <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-orange-400" /></div>
                  ) : (
                    <div className="space-y-2.5 text-sm">
                      <StatRow label="Calendar days in range" value={`${calc.totalDays} days`} />
                      <StatRow label="Working days" value={`${calc.workingDays} days`} />
                      <StatRow label="Present days" value={`${calc.presentDays} days`} tone="emerald" />
                      <StatRow label="Absent days" value={`${calc.absentDays} days`} tone={calc.absentDays > 0 ? 'rose' : 'emerald'} />
                      <StatRow label="Approved leave (unpaid)" value={`${calc.excusedLeaveDays} days`} tone="amber" />
                      <p className="pt-1 text-[10px] text-neutral-400 dark:text-white/40">
                        * Working days exclude Sundays and official holidays. Absent days are working days with no
                        check-in, no approved WFH, and no approved leave. Check-ins on off days do not offset absences.
                        Only days present are paid.
                      </p>
                    </div>
                  )}
                </div>

                {/* Breakdown */}
                <div className="rounded-xl ring-1 ring-inset ring-orange-500/20 bg-orange-500/5 p-4">
                  <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-orange-500 dark:text-orange-400">Breakdown</h4>
                  <div className="space-y-2.5 text-sm">
                    <StatRow label="Monthly stipend" value={rupee(calc.allocatedSalary)} />
                    <StatRow label="Daily stipend" value={`${rupee(calc.dailyRate)}/day`} />
                    <StatRow label="Days paid (present)" value={`${calc.presentDays} of ${calc.workingDays}`} tone="emerald" />
                    <StatRow label="Earned (present × daily)" value={rupee(calc.earnedPay)} />
                    <StatRow
                      label={`Absence deduction (${calc.absentDays} × daily)`}
                      value={calc.absenceDeduction > 0 ? `-${rupee(calc.absenceDeduction)}` : '—'}
                      tone={calc.absenceDeduction > 0 ? 'rose' : undefined}
                    />
                    <StatRow
                      label="Reductions"
                      value={calc.reductions > 0 ? `-${rupee(calc.reductions)}` : '—'}
                      tone={calc.reductions > 0 ? 'rose' : undefined}
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <p className="text-[10px] text-neutral-400 dark:text-white/30">
                  Formula: Daily Stipend = Monthly Stipend ÷ Working Days · Final Payable = Daily Stipend × Present
                  Days − Reductions. Absence Deduction (Daily × Absent Days) is reported for reference; approved leave
                  days are unpaid.
                </p>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
                  <Button
                    onClick={downloadCurrentInvoice}
                    disabled={!calcUserId || calc.allocatedSalary <= 0}
                    className="bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white shadow-lg shadow-emerald-500/20"
                  >
                    <FileDown className="h-4 w-4 mr-2" />Download Invoice (PDF)
                  </Button>
                  <Button
                    onClick={saveCalculation}
                    disabled={savingCalc || !calcUserId}
                    className="bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white shadow-lg shadow-orange-500/20"
                  >
                    {savingCalc
                      ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</>
                      : <><Save className="h-4 w-4 mr-2" />Save calculation</>}
                  </Button>
                </div>
              </div>
              <p className="text-[10px] text-neutral-400 dark:text-white/30">
                Saving records this calculation against {calcStartDate.slice(0, 7)} and updates that month&apos;s stipend report.
              </p>
            </div>
          </div>
        </div>
      </GlassCard>

      <GlassCard>
        <GlassCardHeader>
          <GlassCardTitle>Monthly Stipends</GlassCardTitle>
          <span className="text-xs text-neutral-400 dark:text-white/40">{finalizedCount}/{reports.length} finalized</span>
        </GlassCardHeader>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
        ) : reports.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Calculator className="h-12 w-12 text-white/20 mb-3" />
            <p className="text-sm text-neutral-400 dark:text-white/40 mb-4">No stipend reports for {month}.</p>
            <Button onClick={() => generate()} disabled={generating} className="bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white shadow-lg shadow-orange-500/20">
              {generating ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Generating...</> : <><Calculator className="h-4 w-4 mr-2" />Generate Stipend Reports</>}
            </Button>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <div className="min-w-[980px]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-neutral-200 dark:border-white/10 hover:bg-transparent">
                      <TableHead className="text-neutral-500 dark:text-white/50">Name</TableHead>
                      <TableHead className="text-neutral-500 dark:text-white/50">Status</TableHead>
                      <TableHead className="text-neutral-500 dark:text-white/50">Hours</TableHead>
                      <TableHead className="text-neutral-500 dark:text-white/50">Tasks</TableHead>
                      <TableHead className="text-neutral-500 dark:text-white/50">Overall</TableHead>
                      <TableHead className="text-neutral-500 dark:text-white/50">Tier</TableHead>
                      <TableHead className="text-neutral-500 dark:text-white/50">Suggested</TableHead>
                      <TableHead className="text-neutral-500 dark:text-white/50">Final</TableHead>
                      <TableHead className="text-neutral-500 dark:text-white/50 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reports.map(report => (
                      <TableRow key={report.id} className="border-neutral-100 dark:border-white/5 hover:bg-neutral-50 dark:hover:bg-white/5">
                        <TableCell>
                          <button
                            type="button"
                            onClick={() => setHistoryUser({ id: report.userId, name: report.userName })}
                            title={`View every saved stipend report for ${report.userName}`}
                            className="text-left font-medium text-neutral-900 dark:text-white/90 hover:text-orange-500 dark:hover:text-orange-400 hover:underline underline-offset-2 transition-colors"
                          >
                            {report.userName}
                          </button>
                        </TableCell>
                        <TableCell>
                          {report.finalStipend != null ? (
                            <StatusPill tone="green" dot>Finalized</StatusPill>
                          ) : (
                            <StatusPill tone="amber" dot>Review</StatusPill>
                          )}
                        </TableCell>
                        <TableCell className="text-neutral-800 dark:text-white/80">{report.hours.payable ?? report.hours.total}/{report.hours.expected}</TableCell>
                        <TableCell className="text-neutral-800 dark:text-white/80">{report.tasks.completed}</TableCell>
                        <TableCell className="text-neutral-800 dark:text-white/80">{report.scores.overall}</TableCell>
                        <TableCell><StatusPill tone={TIER_TONE[report.tier]}>{report.tier}</StatusPill></TableCell>
                        <TableCell className="text-neutral-800 dark:text-white/80">{rupee(report.suggestedStipend)}</TableCell>
                        <TableCell className="text-neutral-900 dark:text-white/90 font-medium">{report.finalStipend != null ? rupee(report.finalStipend) : '-'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="icon" variant="ghost" title="Review stipend" onClick={() => openReport(report)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" title="Download Invoice (PDF)" onClick={() => exportInvoice(report)} className="text-emerald-400 hover:text-emerald-300">
                              <FileDown className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" title="Export Payslip (print / PDF)" onClick={() => exportPayslip(report)} className="text-orange-400 hover:text-orange-300">
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              title="Download stipend report as Word"
                              onClick={() => exportStipendReportDoc(report, users.find(u => u.id === report.userId) || {})}
                              className="text-blue-500 hover:text-blue-400"
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                            {report.finalStipend == null && (
                              <Button size="icon" variant="ghost" title="Approve suggested stipend" onClick={() => approveSuggested(report)} className="text-emerald-400 hover:text-emerald-300">
                                <CheckCircle2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {reports.map(report => (
                <GlassCard key={report.id} tone={report.finalStipend != null ? 'green' : 'amber'}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() => setHistoryUser({ id: report.userId, name: report.userName })}
                        className="text-neutral-900 dark:text-white/90 font-medium truncate text-left hover:text-orange-500 dark:hover:text-orange-400 hover:underline underline-offset-2 transition-colors"
                      >
                        {report.userName}
                      </button>
                      <div className="flex items-center gap-2 mt-1">
                        <StatusPill tone={TIER_TONE[report.tier]}>{report.tier}</StatusPill>
                        {report.finalStipend != null ? (
                          <StatusPill tone="green" dot>Finalized</StatusPill>
                        ) : (
                          <StatusPill tone="amber" dot>Review</StatusPill>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" className="text-emerald-300 px-2" title="Download Invoice (PDF)" onClick={() => exportInvoice(report)}>
                        <FileDown className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-orange-300 px-2" title="Export payslip" onClick={() => exportPayslip(report)}>
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-blue-400 px-2"
                        title="Download as Word"
                        onClick={() => exportStipendReportDoc(report, users.find(u => u.id === report.userId) || {})}
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-orange-300 px-2" onClick={() => openReport(report)}>
                        <Eye className="h-4 w-4 mr-1" /> View
                      </Button>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-neutral-400 dark:text-white/40">Hours</p>
                      <p className="text-neutral-800 dark:text-white/80">{report.hours.payable ?? report.hours.total}/{report.hours.expected}</p>
                    </div>
                    <div>
                      <p className="text-neutral-400 dark:text-white/40">Tasks</p>
                      <p className="text-neutral-800 dark:text-white/80">{report.tasks.completed}</p>
                    </div>
                    <div>
                      <p className="text-neutral-400 dark:text-white/40">Suggested</p>
                      <p className="text-neutral-800 dark:text-white/80">{rupee(report.suggestedStipend)}</p>
                    </div>
                    <div>
                      <p className="text-neutral-400 dark:text-white/40">Final</p>
                      <p className="text-neutral-900 dark:text-white/90 font-medium">{report.finalStipend != null ? rupee(report.finalStipend) : '—'}</p>
                    </div>
                  </div>
                  {report.finalStipend == null && (
                    <Button size="sm" className="mt-3 w-full bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25" onClick={() => approveSuggested(report)}>
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Approve Suggested
                    </Button>
                  )}
                </GlassCard>
              ))}
            </div>
          </>
        )}
      </GlassCard>

      <Sheet open={!!active} onOpenChange={(open) => !open && setActive(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto bg-white/95 dark:bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-l border-neutral-200 dark:border-neutral-200 dark:border-white/10">
          {active && (
            <>
              <SheetHeader>
                <SheetTitle className="text-neutral-900 dark:text-white/90">{active.userName} <span className="text-neutral-400 dark:text-white/40 font-normal">— {active.month}</span></SheetTitle>
              </SheetHeader>
              <div className="space-y-5 mt-4 px-4 pb-6">
                <div className="grid grid-cols-2 gap-3">
                  <MiniMetric label="Suggested" value={rupee(active.suggestedStipend)} />
                  <MiniMetric label="Final" value={active.finalStipend != null ? rupee(active.finalStipend) : 'Pending'} />
                  <MiniMetric label="Payable Hours" value={`${active.hours.payable ?? active.hours.total}h`} />
                  <MiniMetric label="Tier" value={active.tier} />
                </div>

                <Tabs defaultValue="summary">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="summary">Summary</TabsTrigger>
                    <TabsTrigger value="hours">Hours</TabsTrigger>
                    <TabsTrigger value="tasks">Tasks</TabsTrigger>
                    <TabsTrigger value="calculation">Calculation</TabsTrigger>
                  </TabsList>

                  <TabsContent value="summary" className="mt-4 space-y-3">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <MiniMetric label="Present" value={String(active.attendance.present)} />
                      <MiniMetric label="WFH" value={String(active.attendance.wfh)} />
                      <MiniMetric label="Leave" value={String(active.attendance.leave)} />
                      <MiniMetric label="Holiday" value={String(active.attendance.holiday)} />
                      <MiniMetric label="Absent" value={String(active.attendance.absent)} />
                      <MiniMetric label="Incomplete" value={String(active.attendance.incomplete)} />
                    </div>
                    <p className="text-xs text-neutral-400 dark:text-white/40">
                      {active.attendance.workingDays} working days in {active.month} · overall score {active.scores.overall}
                    </p>
                  </TabsContent>

                  <TabsContent value="hours" className="mt-4 space-y-3">
                    <div className="space-y-2 text-sm">
                      <CalcRow label="Payable hours" value={`${active.hours.payable ?? active.hours.total}h`} strong />
                      <CalcRow label="Attendance hours" value={`${active.hours.total}h`} />
                      <CalcRow label="Expected hours" value={`${active.hours.expected}h`} />
                      {active.stipendBreakdown && (
                        <CalcRow label="Daily work submitted" value={`${active.stipendBreakdown.dailyWorkHours}h`} />
                      )}
                      {active.stipendBreakdown?.holidayHours ? (
                        <CalcRow label="Holiday hours (paid)" value={`${active.stipendBreakdown.holidayHours}h`} />
                      ) : null}
                    </div>

                    {active.stipendBreakdown?.daily?.length ? (
                      <div>
                        <p className="text-sm font-medium text-neutral-900 dark:text-white/90 mb-2">Daily payable ledger</p>
                        {/* Desktop ledger */}
                        <div className="hidden sm:block max-h-64 overflow-y-auto rounded-xl ring-1 ring-inset ring-neutral-200 dark:ring-white/10 bg-neutral-50 dark:bg-white/5">
                          <table className="w-full text-xs">
                            <thead className="bg-neutral-100 dark:bg-neutral-950/70 text-neutral-500 dark:text-white/50 sticky top-0 backdrop-blur">
                              <tr>
                                <th className="text-left p-2">Date</th>
                                <th className="text-right p-2">Attendance</th>
                                <th className="text-right p-2">Daily Work</th>
                                <th className="text-right p-2">Payable</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(active.stipendBreakdown.daily || []).map(day => (
                                <tr key={day.date} className="border-t border-neutral-100 dark:border-white/5">
                                  <td className="p-2 text-neutral-700 dark:text-white/70">
                                    <div>{day.date}</div>
                                    <div className="text-neutral-400 dark:text-white/40">{day.reason}</div>
                                  </td>
                                  <td className="p-2 text-right text-neutral-700 dark:text-white/70">{day.attendanceHours}h</td>
                                  <td className="p-2 text-right text-neutral-700 dark:text-white/70">{day.dailyWorkHours}h</td>
                                  <td className="p-2 text-right text-neutral-900 dark:text-white/90 font-medium">{day.payableHours}h</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {/* Mobile ledger as stack */}
                        <div className="sm:hidden space-y-2 max-h-64 overflow-y-auto">
                          {(active.stipendBreakdown.daily || []).map(day => (
                            <div key={day.date} className="rounded-lg ring-1 ring-inset ring-neutral-200 dark:ring-white/10 bg-neutral-50 dark:bg-white/5 p-2 text-xs">
                              <div className="flex items-center justify-between">
                                <span className="text-neutral-800 dark:text-white/80">{day.date}</span>
                                <span className="text-neutral-900 dark:text-white/90 font-medium">{day.payableHours}h payable</span>
                              </div>
                              <div className="text-neutral-400 dark:text-white/40 mt-1">{day.reason}</div>
                              <div className="mt-1 flex justify-between text-neutral-600 dark:text-white/60">
                                <span>Att {day.attendanceHours}h</span>
                                <span>DW {day.dailyWorkHours}h</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-neutral-400 dark:text-white/40">No payable days recorded for this month.</p>
                    )}
                  </TabsContent>

                  <TabsContent value="tasks" className="mt-4 space-y-2">
                    <p className="text-sm text-neutral-700 dark:text-white/70">
                      {active.tasks.completed} completed · avg turnaround {active.tasks.avgTurnaroundDays} days
                    </p>
                    {active.tasks.completedTitles.length === 0 ? (
                      <p className="text-sm text-neutral-400 dark:text-white/40">No tasks were completed in this month.</p>
                    ) : (
                      <ul className="text-sm list-disc pl-5 text-neutral-700 dark:text-white/70 space-y-0.5">
                        {active.tasks.completedTitles.map((title, index) => <li key={index}>{title}</li>)}
                      </ul>
                    )}
                  </TabsContent>

                  <TabsContent value="calculation" className="mt-4 space-y-3">
                    <div className="space-y-2 text-sm">
                      <CalcRow label="Hourly pay" value={active.stipendBreakdown ? rupee(active.stipendBreakdown.hourlyPay) : 'Re-generate for breakdown'} />
                      <CalcRow label="Task bonus" value={active.stipendBreakdown ? rupee(active.stipendBreakdown.taskPay) : 'Re-generate for breakdown'} />
                      <CalcRow label="Base pay" value={active.stipendBreakdown ? rupee(active.stipendBreakdown.basePay) : 'Re-generate for breakdown'} />
                      <CalcRow label="Performance bonus" value={active.stipendBreakdown ? rupee(active.stipendBreakdown.performanceBonus) : 'Re-generate for breakdown'} />
                      {active.stipendBreakdown?.cappedAt != null ? (
                        <>
                          <CalcRow label="Uncapped total" value={rupee(active.stipendBreakdown.uncappedStipend)} />
                          <CalcRow label="Monthly cap applied" value={rupee(active.stipendBreakdown.cappedAt)} />
                        </>
                      ) : null}
                      <CalcRow label="Suggested stipend" value={rupee(active.suggestedStipend)} strong />
                    </div>
                    {active.stipendBreakdown?.safeguards?.length ? (
                      <div className="rounded-xl ring-1 ring-inset ring-emerald-500/30 bg-emerald-500/10 p-3">
                        <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Underpayment safeguards applied</p>
                        <ul className="mt-2 list-disc pl-5 text-xs text-emerald-800/80 dark:text-emerald-100/80 space-y-0.5">
                          {(active.stipendBreakdown.safeguards || []).map((item, index) => <li key={index}>{item}</li>)}
                        </ul>
                      </div>
                    ) : null}
                  </TabsContent>
                </Tabs>

                {/* Editor stays outside the tabs so the amount is always reachable,
                    whichever tab the reviewer is reading. */}
                <div className="rounded-xl ring-1 ring-inset ring-orange-500/25 bg-orange-500/5 p-4 space-y-3">
                  <p className="text-sm font-semibold text-neutral-900 dark:text-white/90">Finalize stipend</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="finalAmount" className="text-xs uppercase tracking-wider text-neutral-500 dark:text-white/50">Amount (₹)</Label>
                      <Input id="finalAmount" type="number" min="0" value={finalInput} onChange={e => setFinalInput(e.target.value)} placeholder="Final amount" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="deduction" className="text-xs uppercase tracking-wider text-neutral-500 dark:text-white/50">Deduction (₹)</Label>
                      <Input id="deduction" type="number" min="0" value={deductionInput} onChange={e => setDeductionInput(e.target.value)} placeholder="0" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-neutral-600 dark:text-white/60">
                      Payable:{' '}
                      <span className="font-semibold text-orange-600 dark:text-orange-400 tabular-nums">
                        {Number.isFinite(finalPayable) ? rupee(finalPayable) : '—'}
                      </span>
                    </p>
                    <Button onClick={saveFinalStipend} disabled={savingFinal} className="bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white shadow-lg shadow-orange-500/20">
                      {savingFinal ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</> : <><Save className="h-4 w-4 mr-2" />Save</>}
                    </Button>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  {active.finalStipend == null && (
                    <Button onClick={() => approveSuggested(active)} className="bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white shadow-lg shadow-emerald-500/20">
                      <CheckCircle2 className="h-4 w-4 mr-2" />Approve Suggested
                    </Button>
                  )}
                  <Button onClick={() => exportPayslip(active)} className="bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white shadow-lg shadow-orange-500/20">
                    <Download className="h-4 w-4 mr-2" />Export Payslip
                  </Button>
                  <Button onClick={() => exportInvoice(active)} className="bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white shadow-lg shadow-emerald-500/20">
                    <FileDown className="h-4 w-4 mr-2" />Invoice (PDF)
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => exportStipendReportDoc(active, users.find(u => u.id === active.userId) || {})}
                    className="border-neutral-200 dark:border-white/10 text-neutral-800 dark:text-white/80"
                  >
                    <FileText className="h-4 w-4 mr-2" />Download Word
                  </Button>
                  <Button variant="outline" onClick={() => generate([active.userId])} disabled={generating} className="border-neutral-200 dark:border-white/10 text-neutral-800 dark:text-white/80">
                    <RefreshCw className="h-4 w-4 mr-2" />Re-generate
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </PageShell>
  )
}

function WorkflowStep({ icon: Icon, title, text }: { icon: any; title: string; text: string }) {
  return (
    <GlassCard tone="orange">
      <div className="flex gap-3">
        <div className="h-9 w-9 rounded-xl bg-orange-500/10 ring-1 ring-inset ring-orange-500/20 flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4 text-orange-300" />
        </div>
        <div>
          <p className="text-sm font-medium text-neutral-900 dark:text-white/90">{title}</p>
          <p className="text-xs text-neutral-500 dark:text-white/50 mt-1">{text}</p>
        </div>
      </div>
    </GlassCard>
  )
}

function MiniMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl ring-1 ring-inset ring-neutral-200 dark:ring-white/10 bg-neutral-50 dark:bg-white/5 p-3">
      <p className="text-xs text-neutral-400 dark:text-white/40 uppercase tracking-wider">{label}</p>
      <p className="text-base font-semibold text-neutral-900 dark:text-white/90 mt-1">{value}</p>
    </div>
  )
}

function StatRow({ label, value, tone }: { label: string; value: string; tone?: 'emerald' | 'amber' | 'rose' }) {
  const toneClass =
    tone === 'emerald' ? 'text-emerald-600 dark:text-emerald-400'
      : tone === 'amber' ? 'text-amber-600 dark:text-amber-400'
        : tone === 'rose' ? 'text-rose-600 dark:text-rose-400'
          : 'text-neutral-900 dark:text-white/90'
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-neutral-500 dark:text-white/50">{label}</span>
      <span className={`font-semibold tabular-nums ${toneClass}`}>{value}</span>
    </div>
  )
}

function CalcRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between border-b border-neutral-100 dark:border-white/5 pb-2 last:border-0">
      <span className="text-neutral-500 dark:text-white/50">{label}</span>
      <span className={strong ? 'font-semibold text-neutral-900 dark:text-white/90' : 'text-neutral-800 dark:text-white/80'}>{value}</span>
    </div>
  )
}
